import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface RadioProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Label del radio */
  label?: string;
  /** Descripción adicional */
  description?: string;
  /** Si está en estado de error */
  error?: boolean;
  /** Mensaje de error */
  errorMessage?: string;
}

/**
 * Radio - Componente de radio button estilizado
 * 
 * Estilos Tailwind Plus:
 * - appearance-none con custom dot via before pseudo
 * - Dark mode: bg-white/5, border-white/10
 * - Checked: border-primary-600, bg-primary-600 con dot blanco
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  ({ label, description, error, errorMessage, className, id, ...props }, ref) => {
    const radioId = id || `radio-${Math.random().toString(36).substring(2, 9)}`;

    return (
      <div className="relative flex items-start">
        <div className="flex h-6 items-center">
          <input
            {...props}
            ref={ref}
            id={radioId}
            type="radio"
            className={cn(
              'relative size-4 rounded-full border',
              'appearance-none', // Remove default browser styles
              // Light mode default
              'bg-white border-secondary-300',
              // Custom dot (before pseudo)
              'before:absolute before:inset-1 before:rounded-full before:bg-white',
              'not-checked:before:hidden',
              // Checked state
              'checked:border-primary-600 checked:bg-primary-600',
              // Focus
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2',
              // Disabled
              'disabled:border-secondary-300 disabled:bg-secondary-100 disabled:before:bg-secondary-400 disabled:cursor-not-allowed',
              // Dark mode
              'dark:bg-white/5 dark:border-white/10',
              'dark:checked:border-primary-500 dark:checked:bg-primary-500',
              'dark:focus-visible:ring-primary-500 dark:focus-visible:ring-offset-secondary-900',
              'dark:disabled:bg-white/10 dark:disabled:border-white/5 dark:disabled:before:bg-white/20',
              // Error state
              error && 'border-danger-500 focus-visible:ring-danger-500 dark:border-danger-400',
              className
            )}
          />
        </div>
        {(label || description) && (
          <div className="ml-3 text-sm leading-6">
            {label && (
              <label
                htmlFor={radioId}
                className={cn(
                  'font-medium text-secondary-900 dark:text-white',
                  props.disabled && 'opacity-50 cursor-not-allowed',
                  !props.disabled && 'cursor-pointer'
                )}
              >
                {label}
              </label>
            )}
            {description && (
              <p className="text-secondary-500 dark:text-secondary-400 mt-1">{description}</p>
            )}
            {error && errorMessage && (
              <p className="text-danger-600 dark:text-danger-400 mt-1 text-sm">{errorMessage}</p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Radio.displayName = 'Radio';

export interface RadioGroupProps {
  /** Nombre del grupo (para agrupar radios) */
  name: string;
  /** Label del grupo */
  label?: string;
  /** Descripción del grupo */
  description?: string;
  /** Opciones */
  options: Array<{
    value: string;
    label: string;
    description?: string;
    disabled?: boolean;
  }>;
  /** Valor seleccionado */
  value?: string;
  /** Callback cuando cambia */
  onChange?: (value: string) => void;
  /** Layout */
  layout?: 'vertical' | 'horizontal';
  /** Si está deshabilitado */
  disabled?: boolean;
  /** Clase CSS adicional */
  className?: string;
}

/**
 * RadioGroup - Grupo de radio buttons
 */
export function RadioGroup({
  name,
  label,
  description,
  options,
  value,
  onChange,
  layout = 'vertical',
  disabled,
  className,
}: RadioGroupProps) {
  return (
    <fieldset className={className}>
      {label && (
        <legend className="text-sm/6 font-semibold text-secondary-900 dark:text-white">
          {label}
        </legend>
      )}
      {description && (
        <p className="mt-1 text-sm/6 text-secondary-600 dark:text-secondary-400">
          {description}
        </p>
      )}
      <div
        className={cn(
          'mt-4',
          layout === 'vertical' ? 'space-y-4' : 'flex gap-6 flex-wrap'
        )}
      >
        {options.map((option) => (
          <Radio
            key={option.value}
            name={name}
            value={option.value}
            label={option.label}
            description={option.description}
            checked={value === option.value}
            onChange={() => onChange?.(option.value)}
            disabled={disabled || option.disabled}
          />
        ))}
      </div>
    </fieldset>
  );
}








